import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { BaseButton, ModalX } from '../../../../../BaseComponents';

import { useTheme } from '../../../../../theme';
import { CdnIcon } from '@shared/ui';

export interface Props {
	result: 'success' | 'fail';
	onClick?: (reload?: boolean) => void;
	onBackToOrder?: React.DispatchWithoutAction;
	isOpen?: boolean;
	onClose?: () => void;
	successAdditionalBanner?: React.ReactElement;
	title?: React.ReactNode;
	text?: React.ReactNode;
	renderSuccessButtons?: (params: { className: string }) => React.ReactNode;
	renderFailButtons?: (params: { className: string }) => React.ReactNode;
}

const PaymentResult: React.FC<Props> = ({
	result,
	onClick,
	onBackToOrder,
	isOpen = true,
	onClose,
	successAdditionalBanner,
	title,
	text,
	renderSuccessButtons,
	renderFailButtons
}) => {
	const { Payment: css } = useTheme('Entities');
	const { t } = useTranslation('PaymentResult');

	const centerIcon = result === 'fail' ? 'Info/Cancel' : 'Info/Item Icluded';

	const hasBanner = result === 'success' && !!successAdditionalBanner;
	const resultTitle = title ?? (result === 'success' ? t('Payment successfully complete') : t('Payment has failed'));

	const showButtons = (result === 'success' && onClick) || (result === 'fail' && (onBackToOrder || onClick));

	return (
		<ModalX
			classes={{
				paper: cn(css.wrapper, {
					[css.wrapper_withBanner]: hasBanner,
					[css.wrapper_withText]: !!text
				}),
				top_wrapper: css.topWrapper
			}}
			isOpen={isOpen}
			onClose={onClose}
		>
			<div
				className={cn(css.icon_container, {
					[css.icon_fail]: result === 'fail',
					[css.icon_success]: result === 'success'
				})}
			>
				<CdnIcon icon={centerIcon} className={cn(css.center_icon)} />
			</div>
			<div
				className={cn(css.header, {
					[css.failed]: result === 'fail',
					[css.success]: result === 'success'
				})}
			>
				<div className={css.icons}>
					<CdnIcon icon={'Action/Payment'} />
					<div className={css.paymentDots} aria-hidden="true" />
					<CdnIcon icon={'Flight/Direction'} />
				</div>
			</div>

			<div className={css.content}>
				{text ? (
					<div className={css.textWrapper}>
						<div className={css.title}>{resultTitle}</div>
						<div className={css.text}>{text}</div>
					</div>
				) : (
					<div className={css.title}>{resultTitle}</div>
				)}
				{result === 'success' && successAdditionalBanner}

{showButtons && (
					<div className={css.buttons}>
						{result === 'success' && onClick ? (
							renderSuccessButtons ? (
							renderSuccessButtons({ className: css.redirectButton })
						) : (
							<BaseButton
								onClick={() => {
									onClick(true);
								}}
								className={cn(css.redirectButton, css.redirectButton_success)}
							>
								{t('View booking')}
							</BaseButton>
						)
					) : renderFailButtons ? (
						renderFailButtons({ className: css.redirectButton })
						) : (
							<>
								{onBackToOrder && (
									<BaseButton onClick={onBackToOrder} className={css.redirectButton}>
										{t('View booking')}
									</BaseButton>
								)}
								{onClick && (
									<BaseButton
										onClick={() => {
											onClick(true);
										}}
										className={css.redirectButton}
									>
										{t('Try again')}
									</BaseButton>
								)}
							</>
						)}
					</div>
				)}
			</div>
		</ModalX>
	);
};

export default PaymentResult;
